import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'

import { QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE } from '@apps-common/apis/management/unit-task/task-groups'
import {
  useManagementSearchUnitTaskQueueCount,
  useManagementSearchUnitTaskQueueInfinite
} from '@apps-common/apis/management/unit-task/task-queue'
import { UnitListCardSmSkeleton } from '@apps-common/apps/portal/pages/units/components'
import { useTaskQueueUpdates } from '@apps-common/apps/portal/pages/units/pages/program-tasks/hooks'
import { TaskQueueHeader } from '@apps-common/apps/portal/pages/units/pages/program-tasks/pages/task-queue/components'
import { TaskQueueCard } from '@apps-common/apps/portal/pages/units/pages/program-tasks/pages/task-queue/components/tasks-queue-infinite'
import { InfiniteLoader, PullRefresh } from '@apps-common/components/common'
import { LayoutContainer, LayoutPadding } from '@apps-common/components/layout'
import { PageClearFilterButton, PageResultsCount } from '@apps-common/components/page'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { queryClient } from '@apps-common/lib'
import { useUnitTaskQueueFilterStore } from '@apps-common/stores/useUnitTaskQueueFilterStore'

const containerId = 'tasks-queue-infinite-container'

interface IProps {
  taskGroupId?: number
}

export const TasksQueueInfiniteView: React.FC<IProps> = ({ taskGroupId }) => {
  const filter = useUnitTaskQueueFilterStore()

  useEffect(() => {
    filter.setSkip(0)
  }, [])

  const {
    isLoading: countIsLoading,
    // isFetching: countIsFetching,
    // isRefetching: countIsRefetching,
    isError: countIsError,
    data: countData,
    refetch: countRefetch
  } = useManagementSearchUnitTaskQueueCount({
    taskGroupId: taskGroupId || 0,
    model: filter
  })

  const {
    isLoading: queueLoading,
    isFetching: queueFetching,
    isRefetching: queueRefetching,
    isError: queueError,
    data: queue,
    refetch: queueRefetch,
    fetchNextPage: queueFetchNext,
    hasNextPage: queueHasNextPage
  } = useManagementSearchUnitTaskQueueInfinite({
    enabled: !Number.isNaN(taskGroupId),
    taskGroupId: taskGroupId || 0,
    model: filter
  })

  const data = useMemo(() => queue?.pages?.reduce((acc, curr) => [...acc, ...curr], []) || [], [queue])

  const allLoading = queueLoading || queueFetching || queueError || countIsLoading || countIsError

  useTaskQueueUpdates(containerId, queueLoading, allLoading, data)

  const countLoading = countIsLoading || countIsError
  const loading = queueLoading || queueError

  return (
    <>
      <TaskQueueHeader>
        <div className='flex items-center justify-between'>
          <PageResultsCount loading={countLoading} count={countData} extraText={filter.status?.replaceAll('_', ' ')} />
          <PageClearFilterButton filter={filter} filterKeyWordsFunc={filter.getFilteredByKeywords} callback={filter.clear} />
        </div>
      </TaskQueueHeader>
      <LayoutContainer id={containerId}>
        <LayoutPadding topPadding='pt-[40px] sm:pt-[65px] md:pt-[70px]'>
          <PullRefresh
            loading={queueRefetching}
            callback={() => {
              queryClient.removeQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE(filter) })
              queueRefetch()
            }}
          />
          <>
            <div className='grid sm:grid-cols-2 sm:max-w-[1200px] gap-[15px]'>
              <AnimatePresence>
                {loading &&
                  Array.from(Array(25).keys()).map((k, i) => (
                    <motion.div key={`motion-management-unit-list-card-skeleton-${i}`} {...motionFadeIn(0.2)}>
                      <UnitListCardSmSkeleton
                        key={`management-unit-list-skeleton-${k}`}
                        index={i}
                        bgClasses='bg-surface border border-border min-w-full sm:min-w-[200px] lg:min-w-[250px]'
                        textClasses='text-text'
                        iconClasses='text-text'
                        bgSecondaryClasses='bg-skeleton'
                        textSecondaryClasses='text-text'
                        bgTertiaryClasses='bg-skeleton-hi'
                      />
                    </motion.div>
                  ))}
                {!loading &&
                  data.map((q, i) => (
                    <motion.div key={`motion-management-job-list-card-${q?.id}-${i}`} {...motionFadeIn(0.2)}>
                      <TaskQueueCard queue={q} />
                    </motion.div>
                  ))}
                {/* <EndOfResults show={!queueHasNextPage} /> */}
              </AnimatePresence>
            </div>
            <IonInfiniteScroll
              onIonInfinite={async (ev) => {
                await queueFetchNext()
                ev.target.complete()
              }}
            >
              <InfiniteLoader key='queue-management-infinite-loader' loading={queueFetching} />
            </IonInfiniteScroll>
          </>
        </LayoutPadding>
      </LayoutContainer>
    </>
  )
}
